import type { routes } from '@semios/app-platform-banyan-route-definitions'
import {
  getValueTypeFromEmitterType,
  IRRIGATION_ACTIVITY_APPLIED_VALUE_TYPE_PREFIX,
  IRRIGATION_ACTIVITY_PLANNED_VALUE_TYPE_PREFIX,
} from '@semios/app-platform-values-helpers-and-types'
import { DropdownSelectorIrrigationZone } from 'App/Map/PanelDetails/SectionTitleBars/DropdownSelectorIrrigationZone/DropdownSelectorIrrigationZone'
import { getTimezoneForSelectedPropertyOrRegion } from 'App/Map/PanelDetails/_utils/getTimezoneForSelectedPropertyOrRegion'
import { propertyLacksPermissionSectionMaker } from 'App/Map/PanelDetails/_utils/propertyLacksPermissionSectionMaker'
import { selectedPropertyHasPermission } from 'App/Map/PanelDetails/_utils/selectedPropertyHasPermission'
import { getIrrigationStatusColors } from 'App/Map/_utils/getIrrigationStatusColors'
import { getIdAndEmitterTypeFromZoneEmitterTypeKey } from 'App/Map/_utils/irrigationZoneEmitterTypeKeyUtil'
import { NameWithExternalVendorIcons } from 'components/NameWithExternalVendorIcons/NameWithExternalVendorIcons'
import type { StackedChartSection, StackedChartSectionItem } from 'components/StackedChart/types'
import type { TooltipFormatterContextObject } from 'highcharts'
import { translate } from 'i18n/i18n'
import moment from 'moment-timezone'
import { colors } from 'settings/colors'
import { detailsPanelStore } from 'stores/detailsPanelStore/detailsPanelStore'
import { fieldAssetStore } from 'stores/fieldAssetStore/fieldAssetStore'
import type { TSelectedFieldAssetsStoreState } from 'stores/selectedFieldAssetsStore/selectedFieldAssetsStore'
import { getIrrigationZoneEmitterExternalVendorNames } from 'utils/getIrrigationZoneEmitterExternalVendorNames'
import { makeNameEmitterTypeLabel } from 'utils/makeNameEmitterTypeLabel'
import { minutesToHoursAndMinutes } from 'utils/minutesToHoursAndMinutes'
import { unitConverter } from 'utils/unitConverter/unitConverter'
import { getXDateFormat } from '../../_utils/getXDateFormat'
import { IrrigationChartLegend } from '../_utils/IrrigationChartLegend'
import { getAppliedWater } from '../_utils/getAppliedWater'
import {
  getIntervalForCurrentStamp,
  type TIrrigationActivityInterval,
} from '../_utils/getIntervalForCurrentStamp'
import { getTooltipSectionForIntervalsChart } from '../_utils/getTooltipSectionForIntervalsChart'
import { createBlankSeries } from './_utils/createBlankSeries'

const checkPermission = () => selectedPropertyHasPermission({ permission: 'VIEW_IRRIGATION_DETAILS' })

export const irrigationActivityZonesIntervalsChart = ({
  data,
  selectedFieldAssets,
}: {
  data: routes.Values.Response
  selectedFieldAssets: TSelectedFieldAssetsStoreState
}): StackedChartSection | null => {
  const title = translate.phrases.banyanApp('Irrigation Activity by Zone')

  const commonReturnItems = {
    title,
    fieldAssetPicker: <DropdownSelectorIrrigationZone />,
    id: 'stackem-irrigation-activity-chart-semios',
  }

  const { property: propertyId, irrigationZoneEmitter } = selectedFieldAssets

  if (!checkPermission() || !irrigationZoneEmitter || !propertyId)
    return propertyLacksPermissionSectionMaker(commonReturnItems)

  const selectedZoneId = getIdAndEmitterTypeFromZoneEmitterTypeKey(irrigationZoneEmitter)?.irrigationZoneId
  const properties = fieldAssetStore.getState()?.properties ?? {}
  const propertyData = properties[propertyId]

  const irrigationZoneEmittersForCharts = Object.values(propertyData?.irrigationZoneEmitters ?? {}).filter(
    ({ irrigationZoneEmitterId }) => {
      return irrigationZoneEmitterId.startsWith(`${selectedZoneId}`)
    },
  )

  const pointsForProperty = Object.values(propertyData?.points ?? {})
  const values = data?.irrigationZones?.[selectedZoneId]?.values ?? {}
  const timezone = getTimezoneForSelectedPropertyOrRegion({ properties, propertyId })
  const { dateFrom, dateTo } = detailsPanelStore.getState()

  const chartItems = irrigationZoneEmittersForCharts
    .map((irrigationZoneEmitter): StackedChartSectionItem | null => {
      const { emitterType, flowRate, irrigationZoneEmitterId, name } = irrigationZoneEmitter

      const appliedValueType = getValueTypeFromEmitterType(
        IRRIGATION_ACTIVITY_APPLIED_VALUE_TYPE_PREFIX,
        emitterType,
      )

      const plannedValueType = getValueTypeFromEmitterType(
        IRRIGATION_ACTIVITY_PLANNED_VALUE_TYPE_PREFIX,
        emitterType,
      )

      const appliedIntervals = (values[appliedValueType as keyof typeof values]?.[0]?.timeseries ??
        []) as TIrrigationActivityInterval[]

      const plannedIntervals = (values[plannedValueType as keyof typeof values]?.[0]?.timeseries ??
        []) as TIrrigationActivityInterval[]

      const hasPlannedData = plannedIntervals.length > 0

      const blankSeries = createBlankSeries({
        dateFrom,
        dateTo,
        timezone,
        defaultY: 10,
      })

      let onDurationMinutes = 0

      const appliedIntervalsSeries = appliedIntervals.map((s, index: number) => {
        if (s.status === 'on') {
          const periodMinutes = moment.tz(s.endTime, timezone).diff(s.startTime, 'minutes')

          onDurationMinutes += periodMinutes
        }

        return {
          key: `applied_${index}`,
          x: s.startTime ? moment.tz(s.startTime, timezone).valueOf() : moment.tz(timezone).valueOf(),
          x2: s.endTime ? moment.tz(s.endTime, timezone).valueOf() : moment.tz(timezone).valueOf(),
          y: hasPlannedData ? 3 : 10, // if there is no planned data we can squish the chart to save screen real estate
          color: getIrrigationStatusColors(s.status).backgroundColor,
          status: s.status,
          pointWidth: s.status === 'on' ? 24 : 14,
          name: 'Applied' as const,
        }
      })

      const plannedIntervalsSeries = plannedIntervals.map((s, index: number) => {
        return {
          key: `planned_${index}`,
          x: s.startTime ? moment.tz(s.startTime, timezone).valueOf() : moment.tz(timezone).valueOf(),
          x2: s.endTime ? moment.tz(s.endTime, timezone).valueOf() : moment.tz(timezone).valueOf(),
          y: 10,
          color: colors.lightGreen,
          status: s.status,
          pointWidth: s.status === 'on' ? 24 : 14,
          name: 'Planned' as const,
        }
      })

      const flowRateMM = flowRate.mmPerHour
      const totalWaterApplied = onDurationMinutes ? +((onDurationMinutes / 60) * flowRateMM) : 0
      const mergedIntervalsData = [...appliedIntervalsSeries, ...plannedIntervalsSeries]

      return {
        chartConfig: {
          semiosHighchartsAdditions: {
            id: `Irrigation Schedule ${emitterType}`,
            firstForecastTimestamp: +new Date(),
          },
          chart: {
            type: 'spline' as const,
            height: hasPlannedData ? 250 : 150,
          },

          tooltip: {
            xDateFormat: getXDateFormat(false),
            outside: false,
            formatter: function (this: TooltipFormatterContextObject) {
              const timestamp = this.x

              const {
                start: appliedStart,
                end: appliedEnd,
                status,
              } = getIntervalForCurrentStamp({
                currentTimeStamp: timestamp as number,
                intervals: appliedIntervals,
                timezone,
              })

              const formatToUse = 'ddd, MMM D, h:mm A (z)'

              let appliedSection = ''
              let plannedSection = ''

              if (appliedStart) {
                const appliedForInterval =
                  appliedEnd && (appliedEnd.diff(appliedStart, 'minutes') / 60) * flowRateMM

                const toolTipParams = {
                  currentTimeStamp: moment.tz(timestamp, timezone).format(formatToUse),
                  status,
                  appliedWater: getAppliedWater({
                    status,
                    appliedWater: appliedForInterval,
                  }),
                  periodDurationText: appliedEnd
                    ? minutesToHoursAndMinutes(moment.tz(appliedEnd, timezone).diff(appliedStart, 'minutes'))
                    : translate.phrases.templates('-'),
                  periodFromDateText: appliedStart.format(formatToUse) || translate.phrases.templates('-'),
                  periodToDateText: appliedEnd?.format(formatToUse) || translate.phrases.templates('-'),
                  isScheduled: false,
                }

                appliedSection = getTooltipSectionForIntervalsChart(toolTipParams)
              }

              const { start: plannedStart, end: plannedEnd } = getIntervalForCurrentStamp({
                currentTimeStamp: Number(timestamp),
                intervals: plannedIntervals,
                timezone,
              })

              if (plannedStart) {
                const plannedForInterval =
                  plannedEnd && (plannedEnd.diff(plannedStart, 'minutes') / 60) * flowRateMM

                const toolTipParams = {
                  currentTimeStamp: moment.tz(timestamp, timezone).format(formatToUse),
                  title: translate.phrases.banyanApp('Scheduled'),
                  chartColor: colors.lightGreen,
                  status: 'on' as const,
                  appliedWater: getAppliedWater({
                    status: 'on' as const,
                    appliedWater: plannedForInterval,
                  }),
                  periodDurationText: plannedEnd
                    ? minutesToHoursAndMinutes(moment.tz(plannedEnd, timezone).diff(plannedStart, 'minutes'))
                    : translate.phrases.templates('-'),
                  periodFromDateText: plannedStart.format(formatToUse) || translate.phrases.templates('-'),
                  periodToDateText: plannedEnd?.format(formatToUse) || translate.phrases.templates('-'),
                  isScheduled: true,
                }

                plannedSection = getTooltipSectionForIntervalsChart(toolTipParams)
              }

              if (!plannedSection && !appliedSection) return false

              return `
            <div style="font-size:16px;display:flex;flex-direction:column;gap:4px 12px">
              ${plannedSection}
              ${appliedSection}
            </div>`
            },
          },

          yAxis: {
            min: 0,
            visible: false,
            labels: {
              style: {
                fontSize: '14px',
              },
            },
          },
          plotOptions: {
            series: {
              states: {
                inactive: {
                  opacity: 1,
                },
              },
            },
          },
          series: [
            {
              color: 'rgba(0, 0, 0, 0)',
              type: 'spline' as const,
              name: 'invisible',
              showInLegend: false,
              data: mergedIntervalsData.length === 0 ? [] : blankSeries,
              enableMouseTracking: true,
              id: 'blank',
            },
            {
              name,
              showInLegend: false,
              data: mergedIntervalsData.length === 0 ? [] : mergedIntervalsData,
              enableMouseTracking: true,
              type: 'xrange' as const,
              id: irrigationZoneEmitterId,
            },
          ],
        },
        childrenUpper: (
          <div
            css={{
              fontWeight: 'bold',
              fontSize: '14px',
              padding: '10px 30px 0 30px',
            }}
          >
            <NameWithExternalVendorIcons
              name={makeNameEmitterTypeLabel({ name, emitterType })}
              externalVendorNames={getIrrigationZoneEmitterExternalVendorNames({
                irrigationZoneEmitter,
                points: pointsForProperty,
              })}
            />
          </div>
        ),
        childrenLower: (
          <div
            css={{
              display: 'flex',
              justifyContent: 'flex-end',
              flexWrap: 'wrap',
            }}
          >
            <div css={{ flex: 1 }}>
              <IrrigationChartLegend showScheduled={true} />
            </div>
            <div
              css={{
                textAlign: 'center',
                fontSize: '14px',
                color: colors.grey900,
                margin: '10px 10px 15px',
                display: 'flex',
              }}
            >
              <div>
                {translate.phrases.templates('{{label}}: {{value}}', {
                  label: translate.phrases.banyanApp('On Duration'),
                  value: minutesToHoursAndMinutes(onDurationMinutes),
                })}
              </div>
              <div css={{ padding: '0 10px' }}>{'|'}</div>
              <div>
                {translate.phrases.templates('{{label}}: {{value}}', {
                  label: translate.phrases.banyanApp('Total Volume'),
                  value: unitConverter
                    .irrigationActivity(totalWaterApplied, { decimalPlaces: 2 })
                    .valueWithSuffix(),
                })}
              </div>
            </div>
          </div>
        ),
      }
    })
    .filter((item) => !!item)

  return {
    ...commonReturnItems,
    items: chartItems,
  }
}
